import { useCallback, useMemo } from "react";

export interface AdminPermissionsResult {
  permissionKeySet: Set<string>;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: readonly string[]) => boolean;
  hasAllPermissions: (permissionKeys: readonly string[]) => boolean;
}

/**
 * Normalizes admin permission access checks behind a stable helper API.
 *
 * @remarks
 * Admin V2 pages frequently evaluate multiple permission combinations while rendering.
 * Centralizing the checks avoids repeated `Set` construction and keeps intent explicit.
 */
export const useAdminPermissions = (
  permissionKeys: readonly string[] | undefined,
): AdminPermissionsResult => {
  const permissionKeySet = useMemo(
    () => new Set(permissionKeys ?? []),
    [permissionKeys],
  );

  const hasPermission = useCallback(
    (permissionKey: string) => permissionKeySet.has(permissionKey),
    [permissionKeySet],
  );

  const hasAnyPermission = useCallback(
    (requiredPermissionKeys: readonly string[]) =>
      requiredPermissionKeys.some((permissionKey) =>
        permissionKeySet.has(permissionKey),
      ),
    [permissionKeySet],
  );

  const hasAllPermissions = useCallback(
    (requiredPermissionKeys: readonly string[]) =>
      requiredPermissionKeys.every((permissionKey) =>
        permissionKeySet.has(permissionKey),
      ),
    [permissionKeySet],
  );

  return {
    permissionKeySet,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
